import { pixelsToMeters, toDisplayKmh, TURN_DURATION_S } from '../constants.ts';
import type { Vec2 } from '../types.ts';
import { tessellateRing } from '../map/ring.ts';
import type { MapDocument } from '../map/types.ts';
import type { TurnFeel } from '../turnFeel.ts';
import { EXPERT_KNOBS, type BotLevelKnobs } from './levels.ts';
import type { RacecraftReport } from './racecraft.ts';
import { runBotRace, type BotTurnLog } from './runRace.ts';

/** Seeds a quick playtest races the profile on. Fixed, so two tries of the same knobs agree. */
export const QUICK_PLAYTEST_SEEDS = [1, 2, 3] as const;
export const QUICK_PLAYTEST_MAX_TURNS = 120;

/**
 * Why a crash happened, from what the planner knew that turn. Every clean click it picks is
 * checked shore-free first, so a crash is either aim error moving a clean click, or a turn with
 * no clean click left (it came in too fast).
 */
export type CrashCause = 'aim' | 'cornered' | 'other';

export type PlaytestCrash = {
  turn: number;
  position: Vec2;
  /** Leg it was sailing: 1 is the run to the first gate; counts on across laps. */
  leg: number;
  speedKmh: number;
  impactKmh: number;
  /** 0 is a graze, 90 head-on. */
  crossingDeg: number;
  cause: CrashCause;
  /** One line: what happened. */
  summary: string;
  /** The knobs and recent turns that likely set it up. */
  factors: string[];
};

/** One turn as the log shows it. */
export type PlaytestTurn = {
  turn: number;
  leg: number;
  speedKmh: number;
  aimOffsetM: number;
  slipTurns: number;
  fallback: boolean;
  crashed: boolean;
};

export type PlaytestRace = {
  seed: number;
  finished: boolean;
  /** Boat-clock ms; an unfinished race counts as the whole turn budget. */
  timeMs: number;
  turns: number;
  crashes: PlaytestCrash[];
  /** Boat position at the start and after every turn, for the map sketch. */
  path: Vec2[];
  log: PlaytestTurn[];
};

/** Turns before a crash that count as its run-up. */
const RUN_UP_TURNS = 3;

const round1 = (value: number) => Math.round(value * 10) / 10;

/** Explains one crash turn from the turns before it and the bot's knobs. */
export function explainCrash(
  entry: BotTurnLog,
  before: readonly BotTurnLog[],
  knobs: BotLevelKnobs,
): Pick<PlaytestCrash, 'cause' | 'summary' | 'factors'> {
  const speed = Math.round(toDisplayKmh(entry.speed));
  const runUp = before.slice(-RUN_UP_TURNS);
  const slipped = runUp.reduce((sum, turn) => sum + turn.slipTurns, 0);
  const factors: string[] = [];

  if (entry.fallback) {
    const brake = knobs.brakeConfidence ?? 1;

    if (brake > 1) {
      factors.push(`Trusts its brakes ×${brake}: it plans to stop shorter than the boat can.`);
    }

    if (knobs.depth < EXPERT_KNOBS.depth) {
      factors.push(`Foresight ${knobs.depth} turns (Expert ${EXPERT_KNOBS.depth}): the bend came into view late.`);
    }

    if (knobs.courseSense === 'distance') {
      factors.push('Distance-only course sense: it does not see bends past its foresight.');
    }

    if (slipped > 0) {
      factors.push(`Slipped ${round1(slipped)} turns over the ${runUp.length} turns before.`);
    }

    const aimed = runUp.filter((turn) => turn.aimOffset > 0);

    if (aimed.length > 0) {
      const worst = Math.max(...aimed.map((turn) => pixelsToMeters(turn.aimOffset)));
      factors.push(`Aim error up to ${round1(worst)} m over the ${runUp.length} turns before.`);
    }

    return {
      cause: 'cornered',
      summary: `No clean click was left at ${speed} km/h: it came in too fast to turn or stop in the water ahead.`,
      factors,
    };
  }

  if (entry.aimOffset > 0) {
    factors.push(`Aim error ${knobs.aimErrorM ?? 0} m, more at speed.`);

    if (slipped > 0) {
      factors.push(`Slipped ${round1(slipped)} turns over the ${runUp.length} turns before.`);
    }

    return {
      cause: 'aim',
      summary: `Aim error put the click ${round1(pixelsToMeters(entry.aimOffset))} m off a clean line, onto the shore, at ${speed} km/h.`,
      factors,
    };
  }

  return {
    cause: 'other',
    summary: `Crashed at ${speed} km/h on a click the exact check had passed.`,
    factors,
  };
}

function playtestRace(
  seed: number,
  result: ReturnType<typeof runBotRace>,
  capMs: number,
  knobs: BotLevelKnobs,
): PlaytestRace {
  const crashes: PlaytestCrash[] = [];

  result.log.forEach((entry, index) => {
    if (!entry.crash) {
      return;
    }

    crashes.push({
      turn: entry.turn,
      position: entry.crash.position,
      leg: entry.progress + 1,
      speedKmh: Math.round(toDisplayKmh(entry.speed)),
      impactKmh: Math.round(toDisplayKmh(entry.crash.impact)),
      crossingDeg: Math.round(entry.crash.crossingDeg),
      ...explainCrash(entry, result.log.slice(0, index), knobs),
    });
  });

  return {
    seed,
    finished: result.finished,
    timeMs: result.timeMs ?? capMs,
    turns: result.turns,
    crashes,
    path: result.boats.map((boat) => ({ x: boat.position.x, y: boat.position.y })),
    log: result.log.map((entry) => ({
      turn: entry.turn,
      leg: entry.progress + 1,
      speedKmh: Math.round(toDisplayKmh(entry.speed)),
      aimOffsetM: round1(pixelsToMeters(entry.aimOffset)),
      slipTurns: round1(entry.slipTurns),
      fallback: entry.fallback,
      crashed: entry.crash !== null,
    })),
  };
}

export type PaceReport = {
  mapId: string;
  /** Expert's boat-clock on this map (it ignores the seed, so one race). */
  expertMs: number;
  races: PlaytestRace[];
  /**
   * True when the bot has no slips and no aim error: the seed changes nothing, so it raced once
   * for every seed.
   */
  sameEverySeed: boolean;
  /** Mean of each race's boat-clock over Expert's: 1 is Expert's pace, 1.4 is 40% slower. */
  pace: number;
};

export type MeasurePaceOptions = {
  mapId: string;
  seeds?: readonly number[];
  maxTurns?: number;
  /** Expert's time from an earlier run on this map and feel, to skip its race. */
  expertMs?: number;
  /** The yardstick. Expert unless a test wants a cheaper one. */
  reference?: BotLevelKnobs;
  /** After every turn and race. Expert's race comes first (unless `expertMs` was given). */
  onProgress?: (progress: PlaytestProgress) => void;
};

export type PlaytestProgress = {
  /** Races finished, and races in the run. */
  done: number;
  total: number;
  /**
   * Share of the run done, 0–1: whole races plus the current race's share of its legs (or of
   * the turn budget, if that is further). Each race weighs its search depth, since a turn's think
   * time grows with it: Expert's race is about a third of a Medium run.
   */
  fraction: number;
};

/**
 * Races `knobs` solo on one map, once per seed, and compares its boat-clock with Expert's, as
 * the level calibration does (`scripts/bot-playtest/tune.run.ts`). Solo: no rivals, so this
 * measures pace, not racecraft.
 */
export function measurePace(
  map: MapDocument,
  feel: TurnFeel,
  knobs: BotLevelKnobs,
  options: MeasurePaceOptions,
): PaceReport {
  // Only slips and aim error read the seed; without them every seed sails the same race.
  const sameEverySeed = (knobs.temperature ?? 0) <= 0 && (knobs.aimErrorM ?? 0) <= 0;
  const asked = options.seeds ?? QUICK_PLAYTEST_SEEDS;
  const seeds = sameEverySeed ? asked.slice(0, 1) : asked;
  const maxTurns = options.maxTurns ?? QUICK_PLAYTEST_MAX_TURNS;
  const capMs = maxTurns * TURN_DURATION_S * 1000;
  const total = seeds.length + (options.expertMs === undefined ? 1 : 0);
  let done = 0;

  const reference = options.reference ?? EXPERT_KNOBS;
  const weights = [
    ...(options.expertMs === undefined ? [Math.max(1, reference.depth)] : []),
    ...seeds.map(() => Math.max(1, knobs.depth)),
  ];
  const weightTotal = weights.reduce((sum, weight) => sum + weight, 0);
  const report = (share: number) => {
    const before = weights.slice(0, done).reduce((sum, weight) => sum + weight, 0);
    const current = done < weights.length ? weights[done]! * share : 0;
    options.onProgress?.({ done, total, fraction: Math.min(1, (before + current) / weightTotal) });
  };

  const race = (seed: number, raceKnobs: BotLevelKnobs): PlaytestRace => {
    let turn = 0;
    const result = runBotRace(map, feel, 'expert', seed, {
      mapId: options.mapId,
      maxTurns,
      knobs: raceKnobs,
      // A race that never finishes ends on the budget, so turns count too.
      onTurn: (progress, jobs) => {
        turn += 1;
        const share = Math.max(progress / Math.max(1, jobs), turn / maxTurns);
        report(Math.min(1, share));
      },
    });
    done += 1;
    report(0);
    return playtestRace(seed, result, capMs, raceKnobs);
  };

  const expertMs = options.expertMs ?? race(1, reference).timeMs;
  const races = seeds.map((seed) => race(seed, knobs));
  const pace = races.reduce((sum, entry) => sum + entry.timeMs / expertMs, 0) / races.length;

  return { mapId: options.mapId, expertMs, races, sameEverySeed, pace };
}

/** Which playtest: solo pace (`measurePace`) or the pack against Expert (`measureRacecraft`). */
export type PlaytestKind = 'solo' | 'pack';

/** Page → playtest worker. */
export type PlaytestWorkerInbound = {
  type: 'run';
  id: number;
  /** Default `solo`. */
  kind?: PlaytestKind;
  map: MapDocument;
  mapId: string;
  feel: TurnFeel;
  knobs: BotLevelKnobs;
};

/** Playtest worker → page. */
export type PlaytestWorkerOutbound =
  | ({ type: 'progress'; id: number } & PlaytestProgress)
  | { type: 'result'; id: number; report: PaceReport }
  | { type: 'pack'; id: number; report: RacecraftReport }
  | { type: 'error'; id: number; message: string };

/** A flat picture of a map for the playtest log: land outlines, gates, the start area. */
export type PlaytestSketch = {
  land: Vec2[][];
  gates: { a: Vec2; b: Vec2; goal: boolean }[];
  start: Vec2[];
};

/** Coarse outlines (few segments per corner) keep the log's SVG light. */
export function playtestSketch(map: MapDocument): PlaytestSketch {
  return {
    land: [...map.islands, ...map.rocks].map((ring) => tessellateRing(ring, 2, 4)),
    gates: map.checkpoints.map((checkpoint) => ({
      a: { ...checkpoint.a },
      b: { ...checkpoint.b },
      goal: checkpoint.isGoal,
    })),
    start: map.startArea ? tessellateRing(map.startArea, 2, 4) : [],
  };
}
